import React, { useState, useEffect } from "react";
import { Grid, Icon, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, Divider } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSelect from "components/MDSelect";
import { Form, Formik } from "formik";
import { get, postNoResponse, delNoResponse } from "utils/WebService";
import MDSnackbar from "components/MDSnackbar";

export default function ContasBancariasClienteList({ pessoaId }) {
    const [contasVinculadas, setContasVinculadas] = useState([]);
    const [contasDisponiveis, setContasDisponiveis] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", isSuccess: true });

    useEffect(() => {
        if (pessoaId) {
            fetchContasVinculadas();
        }
    }, [pessoaId]);

    const fetchContasVinculadas = () => {
        setLoading(true);
        get(`/conta-bancaria/cliente/list/${pessoaId}`)
            .then((data) => {
                setContasVinculadas(data || []);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    };

    const fetchContasDisponiveis = () => {
        get(`/conta-bancaria/list-all`)
            .then((data) => {
                setContasDisponiveis(data || []);
            })
            .catch(() => {
                setContasDisponiveis([]);
            });
    };

    const handleOpenDialog = () => {
        fetchContasDisponiveis();
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleSubmit = (values, { setSubmitting }) => {
        if (!values.contaSelecionada) {
            setSnackbar({ open: true, message: "Selecione uma conta bancária.", isSuccess: false });
            setSubmitting(false);
            return;
        }

        const payload = {
            idContaBancaria: values.contaSelecionada.id,
            idPessoa: pessoaId
        };

        postNoResponse("/conta-bancaria/cliente/save", payload)
            .then(() => {
                setSnackbar({ open: true, message: "Conta vinculada com sucesso!", isSuccess: true });
                handleCloseDialog();
                fetchContasVinculadas();
            })
            .catch((err) => {
                setSnackbar({ open: true, message: "Erro ao vincular conta.", isSuccess: false });
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    const handleDeleteVinculo = (idRelacao) => {
        if (window.confirm("Deseja realmente excluir esta associação?")) {
            setLoading(true);
            delNoResponse(`/conta-bancaria/cliente/delete/${idRelacao}`)
                .then(() => {
                    setSnackbar({ open: true, message: "Associação excluída com sucesso!", isSuccess: true });
                    fetchContasVinculadas();
                })
                .catch(() => {
                    setSnackbar({ open: true, message: "Erro ao excluir associação.", isSuccess: false });
                    setLoading(false);
                });
        }
    };

    return (
        <MDBox>
            <Divider sx={{ my: 0 }} />
            <MDBox p={3}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <MDTypography variant="h6" fontWeight="medium">Contas Bancárias Vinculadas</MDTypography>
                    <MDButton variant="gradient" color="info" onClick={() => handleOpenDialog()} startIcon={<Icon>add</Icon>} size="small">
                        Vincular Conta
                    </MDButton>
                </MDBox>

                <TableContainer sx={{ boxShadow: "none" }}>
                <Table>
                    <TableHead sx={{ display: "table-header-group" }}>
                        <TableRow>
                            <TableCell><MDTypography variant="button" fontWeight="medium" color="text">Descrição</MDTypography></TableCell>
                            <TableCell><MDTypography variant="button" fontWeight="medium" color="text">Banco</MDTypography></TableCell>
                            <TableCell><MDTypography variant="button" fontWeight="medium" color="text">Agência</MDTypography></TableCell>
                            <TableCell><MDTypography variant="button" fontWeight="medium" color="text">Conta</MDTypography></TableCell>
                            <TableCell align="center"><MDTypography variant="button" fontWeight="medium" color="text">Ações</MDTypography></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center"><MDTypography variant="button" fontWeight="regular" color="text">Carregando...</MDTypography></TableCell>
                            </TableRow>
                        ) : contasVinculadas.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center"><MDTypography variant="button" fontWeight="regular" color="text">Nenhuma conta bancária vinculada.</MDTypography></TableCell>
                            </TableRow>
                        ) : (
                            contasVinculadas.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell><MDTypography variant="button" fontWeight="regular" color="text">{row.tbContaBancaria?.ccDescricao}</MDTypography></TableCell>
                                    <TableCell><MDTypography variant="button" fontWeight="regular" color="text">{row.tbContaBancaria?.ccBanco}</MDTypography></TableCell>
                                    <TableCell><MDTypography variant="button" fontWeight="regular" color="text">{row.tbContaBancaria?.ccAgencia}-{row.tbContaBancaria?.ccAgenciaDigito}</MDTypography></TableCell>
                                    <TableCell><MDTypography variant="button" fontWeight="regular" color="text">{row.tbContaBancaria?.ccConta}-{row.tbContaBancaria?.ccContaDigito}</MDTypography></TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            color="error"
                                            onClick={() => handleDeleteVinculo(row.cvId)}
                                        >
                                            <Icon>delete</Icon>
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                <DialogTitle>Vincular Conta Bancária</DialogTitle>
                <Formik
                    initialValues={{
                        contaSelecionada: null
                    }}
                    onSubmit={handleSubmit}
                >
                    {({ values, isSubmitting, setFieldValue }) => (
                        <Form>
                            <DialogContent dividers>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <MDTypography variant="button" fontWeight="regular" color="text" sx={{ mb: 1, display: 'block' }}>
                                            Selecione a Conta Bancária
                                        </MDTypography>
                                        <MDSelect
                                            options={contasDisponiveis}
                                            getOptionValue={option => option.id}
                                            getOptionLabel={option => `${option.descricao} (Banco: ${option.banco})`}
                                            value={values.contaSelecionada}
                                            onChange={selectedOption => {
                                                setFieldValue('contaSelecionada', selectedOption);
                                            }}
                                            placeholder="Selecione uma conta..."
                                        />
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions>
                                <MDButton color="secondary" onClick={handleCloseDialog}>
                                    Cancelar
                                </MDButton>
                                <MDButton color="info" type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? "Vinculando..." : "Vincular"}
                                </MDButton>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>

            {snackbar.open && (
                <MDSnackbar
                    color={snackbar.isSuccess ? "success" : "error"}
                    icon={snackbar.isSuccess ? "check" : "warning"}
                    title="Aviso"
                    content={snackbar.message}
                    dateTime=""
                    open={snackbar.open}
                    close={() => setSnackbar({ ...snackbar, open: false })}
                />
            )}
            </MDBox>
        </MDBox>
    );
}
